import { Component, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonSearchbar, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ContactModel,
  ContactStore,
  CONTACT_FILTER_TYPE,
} from '@boxx/contacts-core';
import { FirebaseAnalyticsService } from 'src/services/NativeFirebase/NativeFirebase.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'boxx-contact-picker-modal',
  templateUrl: './contact-picker.modal.html',
  styleUrls: ['./contact-picker.modal.scss'],
})
export class ContactPickerModalComponent implements OnInit, OnDestroy {
  @ViewChild('contactPickerSB', { static: false }) searchBar: IonSearchbar;

  @Input() currentAttendees: number[];
  totalSelected = 0;

  searchValue = '';
  allcontacts: ContactModel[] = [];
  contactList: ContactToPick[] = [];
  searchResults: ContactToPick[];

  filteredContacts$: Observable<ContactModel[]>;
  filterType: CONTACT_FILTER_TYPE = 'ALL';
  isFiltering = false;
  isIndeterminate: boolean;
  masterCheck: boolean;

  destroyed$ = new Subject<boolean>();
  loading$: Observable<boolean>;

  translations: any;

  contactCount: { [k in CONTACT_FILTER_TYPE]: number } = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ALL: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NOT_SPECIFIED: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    PROSPECT: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CLIENT: 0,
  };

  constructor(
    private modalCtrl: ModalController,
    private store: ContactStore,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private firebaseService: FirebaseAnalyticsService,
    private ngZone: NgZone
  ) {
    this.translate
      .get(['CONTACTS'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((t: any) => (this.translations = t));
  }

  ngOnInit() {
    this.loading$ = this.store.contacts.loading$;
    this.filteredContacts$ = this.store.contacts.filteredContacts$;

    this.filteredContacts$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cList) => {
        this.searchResults = this.contactList = cList.map((c) => ({
          isChecked: this.currentAttendees.findIndex((e) => e === c.id) !== -1,
          contact: c,
        }));

        this.setMasterCheckState(
          this.contactList.reduce(
            (prev, curr) => prev + (curr.isChecked ? 1 : 0),
            0
          ),
          this.searchResults.length
        );
      });

    this.store.contacts.data$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cl) => {
        this.allcontacts = cl;

        cl.forEach((contact) => {
          this.contactCount[contact.type]++;
          this.contactCount.ALL++;
        });

        this.filterBy(this.filterType);

        // setTimeout(() => {
        //   this.searchBar.setFocus();
        // }, 800);
      });

    this.store.contacts.fetched$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ok) => {
        if (!ok) {
          this.store.contacts.fetch();
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  checkMaster() {
    setTimeout(() => {
      this.searchResults.forEach((obj) => {
        obj.isChecked = this.masterCheck;
      });
    });
  }

  checkEvent(item: ContactToPick) {
    const totalItems = this.searchResults.length;
    let checked = 0;
    this.searchResults.map((obj) => {
      if (obj.isChecked) {
        checked++;
      }
    });

    this.totalSelected = checked;
    this.setMasterCheckState(checked, totalItems);

    this.ngZone.run(() => {
      const isInArray =
        this.currentAttendees.find((el) => el === item.contact.id) !== undefined;
      if (item.isChecked && !isInArray) {
        this.currentAttendees.push(item.contact.id);
      } else if (!item.isChecked) {
        const index = this.currentAttendees.indexOf(item.contact.id);
        if (index !== -1) {
          this.currentAttendees.splice(index, 1);
        }
      }
    });

  }

  setMasterCheckState(checked: number, totalItems: number) {
    if (checked > 0 && checked < totalItems) {
      // If even one item is checked but not all
      this.isIndeterminate = true;
      this.masterCheck = false;
    } else if (checked === totalItems) {
      // If all are checked
      this.masterCheck = true;
      this.isIndeterminate = false;
    } else {
      // If none is checked
      this.isIndeterminate = false;
      this.masterCheck = false;
    }
  }

  async close() {
    if (this.hasChanges()) {
      (
        await this.alertCtrl.create({
          header: this.translations.ACTIONS.confirm,
          message:
            this.translations.CONTACTS.confirmCloseWithSelectedContactsMsg,
          buttons: [
            {
              text: this.translations.ACTIONS.ok,
              handler: () => {
                this.modalCtrl.dismiss(/*{someData: ...} */);
              },
            },
            {
              text: this.translations.ACTIONS.cancel,
              role: 'cancel',
              cssClass: 'primary',
            },
          ],
        })
      ).present();
    } else {
      this.modalCtrl.dismiss(/*{someData: ...} */);
    }
  }

  select() {
    return this.modalCtrl.dismiss(this.getSelectedContacts());
  }

  hasChanges() {
    return (
      this.getSelectedContacts().length !== this.currentAttendees.length ||
      this.getSelectedContacts().filter(
        (c) => !this.currentAttendees.includes(c.id)
      ).length > 0
    );
  }

  search(event: any) {
    const searchTerm = event.detail.value;
    this.searchResults = this.contactList;

    if (!searchTerm) {
      this.isFiltering = false;
      return;
    }

    this.isFiltering = true;

    this.searchResults = this.searchResults.filter(
      (item) =>
        (item.contact.name + item.contact.lastName)
          .toLowerCase()
          .indexOf(searchTerm.toLowerCase()) > -1 ||
        item.contact.phone.indexOf(searchTerm) > -1 ||
        item.contact.email.indexOf(searchTerm) > -1
    );
  }

  showAll() {
    this.searchValue = ' ';
    setTimeout(() => {
      this.searchValue = '';
    }, 10);
  }

  filterBy(criteria: any = this.filterType) {
    if (!criteria) {
      return;
    }

    this.searchValue = ' ';
    setTimeout(() => {
      this.searchValue = '';
    }, 10);

    //this.showSearchBar = false;
    this.store.contacts.filter(criteria);
  }

  private getSelectedContacts() {
    return this.currentAttendees.map((id) =>
      this.allcontacts.find((contact) => contact.id === id)
    );
    //return this.searchResults.filter((c) => c.isChecked).map((c) => c.contact);
  }

  async presentModal(
    options: { mode: 'CREATE' | 'UPDATE'; contact: ContactModel } = {
      mode: 'CREATE',
      contact: ContactModel.empty(),
    }
  ) {
    // const modal = await this.modalCtrl.create({
    //   component: ContactUpsertModalComponent,
    //   componentProps: {
    //     mode: options.mode,
    //     contact: options.contact,
    //   },
    //   cssClass: 'modalUpsert',
    // });

    // this.firebaseService.setScreenName('CONTACTS > ' + options.mode);

    // await modal.present();

    // const { data } = await modal.onWillDismiss();

    // if (options.mode === 'CREATE') {
    //   this.filterType = 'ALL';
    // }
  }

}


interface ContactToPick {
  isChecked: boolean;
  contact: ContactModel;
}
